import type { Anchors } from "../lib/api.js";
import { el } from "../lib/dom.js";
import { anchorsFor, clampGap, gapAt, groupedAnchors, moves, visibleAnchors, type Placed } from "../lib/reorder.js";

/**
 * How far a pointer must move before a press on the handle becomes a drag.
 *
 * Without it a tap that wobbles by a pixel commits a move nobody asked for,
 * and on a touch screen no tap is perfectly still.
 */
const DRAG_THRESHOLD = 6;

/**
 * How far outside the list still counts as a drop.
 *
 * `:159` says leaving a valid drop region cancels without mutating, and a
 * region with no tolerance at all would cancel a drag whose pointer strayed a
 * few pixels past the edge of a row it was plainly aiming at.
 */
const DROP_SLACK = 40;

/** How close to the edge of the viewport starts an auto-scroll, and how fast. */
const EDGE_ZONE = 80;
const EDGE_MAX = 18;

/** Pointer reordering shared by items and the Lists overview. */
export function reorderController(options: {
  root: HTMLElement;
  rows: HTMLElement;
  footer: HTMLElement;
  nodeFor(id: string): HTMLElement | undefined;
  all(): readonly { id: string }[];
  fallback(): HTMLElement;
  announce(message: string): void;
  changedMessage: string;
  move(id: string, anchors: Anchors, options: { undoable?: boolean }): void;
  /**
   * Furniture that overlaps the rows, where a release is never a drop: the
   * phone's sticky Reorder bar (g53), which rows scroll under. The footer is
   * always one.
   */
  obstacles?(): readonly HTMLElement[];
  /**
   * The rows a drag of this one is held among, drawn or not (g81): its
   * category's, while a list is ordered by category. The marker stops at the
   * category's ends and a drop there lands inside it. Absent, or answering
   * nothing, a row may go anywhere.
   */
  group?(id: string): ReadonlySet<string> | undefined;
}) {
  const { root: node, rows, footer: addButton, announce } = options;
  const marker = el("div", { class: "drop-marker", attrs: { hidden: true, "aria-hidden": "true" } });
    interface Dragging {
      id: string;
      handle: HTMLElement;
      node: HTMLElement;
      pointerId: number;
      startY: number;
      /** False until the pointer has moved far enough to mean it. */
      live: boolean;
      /** Undefined while the pointer is outside a valid drop region. */
      gap?: number;
      /** The floating copy that follows the pointer. */
      ghost?: HTMLElement;
      /** Where in the row the pointer took hold, so the ghost does not jump. */
      grabOffset: number;
      /** The rAF handle for edge auto-scroll. */
      frame?: number;
      x: number;
      y: number;
      /**
       * The rows on screen, and every row in the list, as the press found
       * them. `listChanged` compares against these: a drop is resolved against
       * whatever is drawn when the pointer comes up, so a list that changed
       * shape mid-gesture would land the row somewhere the person never aimed.
       */
      rows: string[];
      all: string[];
      /** The rows this drag is held among, as the press found them. See `options.group`. */
      group?: ReadonlySet<string>;
    }

    let drag: Dragging | undefined;
    /** The rows as drawn, with the geometry a drop is resolved against. */
    function placedRows(): Placed[] {
      return [...rows.children].flatMap((node) => {
        const id = node instanceof HTMLElement ? node.dataset["id"] : undefined;
        if (id === undefined) return [];
        const box = node.getBoundingClientRect();
        return [{ id, top: box.top, bottom: box.bottom }];
      });
    }

    /** The rows as drawn, in order — what a step walks among. */
    function renderedIds(): { id: string }[] {
      return [...rows.children].flatMap((node) => {
        const id = node instanceof HTMLElement ? node.dataset["id"] : undefined;
        return id === undefined ? [] : [{ id }];
      });
    }

    /**
     * Dragging, from the handle only.
     *
     * Everything that needs real layout is here and nothing that needs it is
     * tested in jsdom, which has none: `getBoundingClientRect` returns zeros
     * and there is no `elementFromPoint`. What is worth getting right — which
     * gap a pointer is over, which neighbours that names, whether the drop
     * moves anything at all — is in `lib/reorder.ts`, where a test can hand it
     * the geometry. This is the plumbing around that.
     */
    function startDragging(handle: HTMLElement, idOf: () => string): void {
      // A press here never scrolls the list: `touch-action: none` on the handle
      // means the browser hands the whole gesture to script rather than panning
      // with it. That is the design system's rule (`:159`) — disabled on the
      // handle and nowhere else — and it is why the threshold below is about
      // not committing a move, not about letting a scroll through.
      handle.addEventListener("pointerdown", (event: PointerEvent) => {
        // The primary button only, and never a second finger mid-drag.
        if (drag || event.button !== 0) return;
        const id = idOf();
        const node = options.nodeFor(id);
        if (!node || renderedIds().length < 2) return;
        // A row being edited has no handle to press (g54): its handle is
        // folded away so the editor has the width. A press that arrives
        // anyway — the frame before the style lands — moves nothing.
        if (node.classList.contains("editing")) return;

        drag = {
          id,
          handle,
          node,
          pointerId: event.pointerId,
          startY: event.clientY,
          x: event.clientX,
          y: event.clientY,
          grabOffset: event.clientY - node.getBoundingClientRect().top,
          live: false,
          rows: renderedIds().map((row) => row.id),
          all: options.all().map((row) => row.id),
          group: options.group?.(id),
        };
        // Capture, so the drag survives the pointer leaving the handle — which
        // it does immediately, because the row moves and the finger does not.
        try {
          handle.setPointerCapture(event.pointerId);
        } catch {
          // Not every environment has capture; the document-level listeners
          // below are what actually carry the drag, so this is an improvement
          // rather than a requirement.
        }
      });

      // The browser took the capture away — another window, a system gesture
      // (O04 names lost capture beside Escape and pointer cancel). The release
      // that `endDrag` itself makes fires this too, by which time `drag` is
      // gone and this does nothing, as does the implicit release after every
      // pointerup, which arrives after `onUp` has already committed.
      handle.addEventListener("lostpointercapture", (event: PointerEvent) => {
        if (drag && event.pointerId === drag.pointerId) endDrag(false);
      });
    }

    function dragMove(event: PointerEvent): void {
      if (!drag || event.pointerId !== drag.pointerId) return;

      if (!drag.live) {
        // A threshold, so a tap on the handle is a tap and not a one-pixel
        // drag that commits a move nobody asked for.
        if (Math.abs(event.clientY - drag.startY) < DRAG_THRESHOLD) return;
        drag.live = true;
        drag.node.classList.add("dragging");
        rows.classList.add("reordering");
        lift(drag);
        announce("Reordering. Escape cancels.");
        drag.frame = requestAnimationFrame(edgeScroll);
      }

      // `touch-action: none` on the handle has already told the browser not to
      // pan from this target, so this is not what keeps the list scrollable —
      // the handle being the *only* thing with that property is. What this
      // prevents is the pointer's default text-selection behaviour once a drag
      // is plainly under way.
      event.preventDefault();
      drag.x = event.clientX;
      drag.y = event.clientY;
      follow(drag);
      locate(drag);
    }

    /**
     * The floating row the design system asks for by name (`:159`): "a floating
     * row and insertion line show the pending placement".
     *
     * A clone rather than the row itself, so the list keeps its shape while the
     * copy moves: lifting the real row out of flow would collapse the gap it
     * came from and make every other row jump the instant a drag began.
     */
    function lift(current: Dragging): void {
      const box = current.node.getBoundingClientRect();
      const ghost = current.node.cloneNode(true) as HTMLElement;
      ghost.classList.add("drag-ghost");
      ghost.classList.remove("dragging");
      ghost.removeAttribute("data-id");
      ghost.setAttribute("aria-hidden", "true");
      ghost.inert = true;
      // Nothing inside a copy should be reachable: two checkboxes for one item,
      // one of which writes nowhere, is worse than no copy at all.
      for (const control of ghost.querySelectorAll("input, button, textarea, select, a")) {
        control.setAttribute("tabindex", "-1");
        control.setAttribute("aria-hidden", "true");
        (control as HTMLInputElement).disabled = true;
      }
      ghost.style.width = `${box.width}px`;
      ghost.style.left = `${box.left}px`;
      current.ghost = ghost;
      node.append(ghost);
      follow(current);
    }

    function follow(current: Dragging): void {
      if (current.ghost) current.ghost.style.top = `${current.y - current.grabOffset}px`;
    }

    /**
     * Which gap the pointer is over, or none at all.
     *
     * `gapAt` answers for any y, including one far outside the list — so the
     * region check has to be here. `:159` requires that leaving a valid drop
     * region cancels without mutating, and without this a release anywhere on
     * the page would commit the row to whichever end it was nearest.
     */
    function locate(current: Dragging): void {
      const box = rows.getBoundingClientRect();
      // The bottom allowance reaches into the footer's first pixels. Keep
      // the gap usable, but never treat a drop on the creation button as a move,
      // nor one on the Reorder bar the rows scroll under.
      const blocked = [addButton, ...(options.obstacles?.() ?? [])].some(obstacle => {
        if (obstacle.hidden) return false;
        const area = obstacle.getBoundingClientRect();
        return current.x >= area.left && current.x <= area.right &&
          current.y >= area.top && current.y <= area.bottom;
      });
      const inside =
        current.y >= box.top - DROP_SLACK &&
        current.y <= box.bottom + DROP_SLACK &&
        current.x >= box.left - DROP_SLACK &&
        current.x <= box.right + DROP_SLACK && !blocked;

      if (!inside) {
        current.gap = undefined;
        marker.hidden = true;
        return;
      }

      const placed = placedRows();
      const over = gapAt(placed, current.id, current.y);
      // Held inside the row's category while the list is ordered by one (g81).
      const gap = current.group ? clampGap(placed, current.id, over, current.group) : over;
      current.gap = gap;
      showMarker(placed, current.id, gap);
    }

    /**
     * Edge auto-scroll, so a long list can be dragged through.
     *
     * Without it the rows off the bottom of the screen cannot be reached at
     * all: the pointer stops at the edge and the list never moves under it.
     * The bottom threshold clears the phone's fixed navigation, which is
     * otherwise where the last inch of usable travel goes.
     */
    function edgeScroll(): void {
      if (!drag?.live) return;
      const top = EDGE_ZONE;
      const bottom = window.innerHeight - (window.innerWidth < 768 ? EDGE_ZONE + 56 : EDGE_ZONE);
      const speed =
        drag.y < top
          ? -Math.min(EDGE_MAX, (top - drag.y) / 4)
          : drag.y > bottom
            ? Math.min(EDGE_MAX, (drag.y - bottom) / 4)
            : 0;
      if (speed !== 0) {
        window.scrollBy(0, speed);
        // The rows have moved under a pointer that has not, so the gap and the
        // marker are both stale until this runs again.
        locate(drag);
      }
      drag.frame = requestAnimationFrame(edgeScroll);
    }

    function showMarker(placed: Placed[], id: string, gap: number): void {
      const others = placed.filter((row) => row.id !== id);
      const edge = others[gap]?.top ?? others[others.length - 1]?.bottom;
      if (edge === undefined) return;
      marker.hidden = false;
      marker.style.top = `${edge}px`;
      const box = rows.getBoundingClientRect();
      marker.style.left = `${box.left}px`;
      marker.style.width = `${box.width}px`;
    }

    /**
     * Ends a drag. `commit` is false for Escape, pointer-cancel and blur;
     * `reason` is "changed" when the list moved under the gesture.
     */
    function endDrag(commit: boolean, reason?: "changed"): void {
      if (!drag) return;
      const finished = drag;
      drag = undefined;
      marker.hidden = true;
      finished.ghost?.remove();
      if (finished.frame !== undefined) cancelAnimationFrame(finished.frame);
      finished.node.classList.remove("dragging");
      rows.classList.remove("reordering");
      try {
        finished.handle.releasePointerCapture(finished.pointerId);
      } catch {
        // See `startDragging`.
      }

      // Back to the control they were using, not the top of the document —
      // or, when the list took that row away, to the nearest handle still
      // drawn. After the commit, not before it: `relocate` lifts the row out
      // of the DOM and puts it back, and a node removed while focused hands
      // the focus to the body. A press that never became a drag says nothing,
      // but its focus must not be left on a detached node either.
      const land = () => (finished.handle.isConnected ? finished.handle : landingFor(finished)).focus();

      if (!finished.live) {
        land();
        return;
      }
      if (reason === "changed") {
        announce(options.changedMessage);
      } else if (!commit || finished.gap === undefined) {
        announce("Reorder cancelled. Position unchanged.");
      } else {
        const placed = placedRows();
        // A drop where it started sends nothing. It would still bump
        // `updated_at`, push the row into every other device's delta, and
        // offer to undo something that did not happen.
        if (moves(placed, finished.id, finished.gap)) {
          // The gap names the visible row below it; the full list names the
          // real neighbour above, which may be a hidden one (C03, §8).
          const { beforeId } = anchorsFor(placed, finished.id, finished.gap);
          const anchors = finished.group
            ? groupedAnchors(options.all(), finished.group, finished.id, beforeId ?? null)
            : visibleAnchors(options.all(), finished.id, beforeId ?? null);
          options.move(finished.id, anchors, { undoable: true });
        }
      }
      land();
    }

    /**
     * Where the focus goes when the list took the dragged row away: the next
     * handle still drawn, else the previous, else List options — the shape
     * `removeRows` and a deletion's `lostFocus` already follow. Walks the
     * snapshot rather than the DOM, because the row is no longer in it, and
     * skips rows that left in the same pass.
     */
    function landingFor(finished: Dragging): HTMLElement {
      const at = finished.rows.indexOf(finished.id);
      const handleOf = (id: string) =>
        options.nodeFor(id)?.querySelector<HTMLElement>(".item-handle") ?? undefined;
      const after = finished.rows.slice(at + 1).map(handleOf).find(Boolean);
      const before = finished.rows.slice(0, at).reverse().map(handleOf).find(Boolean);
      return after ?? before ?? options.fallback();
    }

    /**
     * Cancels a drag the list has changed under (spec §8: "if remote changes
     * invalidate the drop anchors while dragging, cancel and explain that the
     * list changed").
     *
     * A comparison of shape rather than a hook in each of `place`, `relocate`,
     * `removeRows` and the delete branch, because two cases have no drawn-row
     * hook at all: a hidden completed row deleted or moved on another device
     * changes the full list, which `visibleAnchors` reads at drop time, without
     * a single row on screen moving. Text edits, due dates and a tick that
     * keeps its row drawn change neither sequence, and the drag carries on.
     * The drop's own write cannot land here: `endDrag` clears `drag` before
     * `commitMove` runs.
     */
    function listChanged(): void {
      if (!drag) return;
      const same = (was: readonly string[], now: readonly { id: string }[]) =>
        was.length === now.length && was.every((id, at) => id === now[at]?.id);
      // A category that gained or lost a row, hidden ones included, is a
      // different set of gaps than the one this drag was held to (g81).
      const now = options.group?.(drag.id);
      const held = drag.group;
      const sameGroup = (held === undefined) === (now === undefined)
        && (!held || !now || (held.size === now.size && [...held].every((id) => now.has(id))));
      if (same(drag.rows, renderedIds()) && same(drag.all, options.all()) && sameGroup) return;
      endDrag(false, "changed");
    }

    // On the document, not the handle: see `View.release`. `pointermove` is
    // not passive because a live drag has to prevent the page scrolling under
    // the row it is moving.
    const onMove = (event: PointerEvent) => dragMove(event);
    const onUp = (event: PointerEvent) => {
      if (drag && event.pointerId === drag.pointerId) endDrag(true);
    };
    const onCancel = (event: PointerEvent) => {
      if (drag && event.pointerId === drag.pointerId) endDrag(false);
    };
    const onKey = (event: KeyboardEvent) => {
      // Escape cancels without mutating, which is what makes a drag safe to
      // start: nothing is written until the pointer comes up somewhere valid.
      if (drag && event.key === "Escape") {
        event.preventDefault();
        endDrag(false);
      }
    };
    // A drag whose window loses focus is a drag nobody is steering any more.
    const onBlur = () => endDrag(false);

    document.addEventListener("pointermove", onMove, { passive: false });
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onCancel);
    document.addEventListener("keydown", onKey);
    window.addEventListener("blur", onBlur);


  return {
    marker,
    bind: startDragging,
    changed: listChanged,
    cancel: () => endDrag(false),
    active: () => drag !== undefined,
    dragging: (id: string) => drag?.id === id && drag.live,
    dispose() {
      endDrag(false);
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onCancel);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("blur", onBlur);
    },
  };
}
